import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiUser, FiMail, FiLock, FiCheck, FiX, FiCamera, FiSave, FiAlertCircle } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const AVATAR_PRESETS = [
  'https://images.unsplash.com/photo-1614850523296-d8c1af93d400?auto=format&fit=crop&w=150&q=80', // Vibrant Wave
  'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=150&q=80', // Pink/Blue Abstract
  'https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?auto=format&fit=crop&w=150&q=80', // Gradient Mesh
  'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=150&q=80', // Deep Blue Pattern
  'https://images.unsplash.com/photo-1574169208507-84376144848b?auto=format&fit=crop&w=150&q=80', // Orange/White Abstract
  'https://images.unsplash.com/photo-1550684848-fac1c5b4e853?auto=format&fit=crop&w=150&q=80', // Red Geometric
  'https://images.unsplash.com/photo-1558478551-1a378f63ad28?auto=format&fit=crop&w=150&q=80', // Yellow/Green
  'https://images.unsplash.com/photo-1563089145-599997674d42?auto=format&fit=crop&w=150&q=80', // Vaporwave Glitch
  'https://images.unsplash.com/photo-1614850523459-c2f4c699c52e?auto=format&fit=crop&w=150&q=80', // Rainbow Liquid
  'https://images.unsplash.com/photo-1620121692029-d088224ddc74?auto=format&fit=crop&w=150&q=80', // Purple Abstract
  'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=150&q=80', // Soft Gradient
  'https://images.unsplash.com/photo-1617419250411-98aa962b070f?auto=format&fit=crop&w=150&q=80', // Teal Geometry
];

const AccountDropdown = ({ isOpen, setIsOpen, user, profile, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'password'
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Form States
  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setAvatarUrl(profile.avatar_url || '');
    }
  }, [profile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error: updateError } = await supabase
      .from('profiles_1716182400000')
      .update({
        full_name: fullName,
        avatar_url: avatarUrl,
        updated_at: new Date(),
      })
      .eq('id', user.id);

    if (updateError) {
      setError(updateError.message);
    } else {
      setSuccess(true);
      onUpdate();
      setTimeout(() => setSuccess(false), 3000);
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    setError(null);

    const { error: pwError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (pwError) {
      setError(pwError.message);
    } else {
      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setSuccess(false), 3000);
    }
    setLoading(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-50" onClick={() => setIsOpen(false)} />
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute top-full right-0 mt-4 w-96 bg-white rounded-3xl shadow-2xl border border-cream-dark z-[60] overflow-hidden text-espresso"
          >
            {/* Header */}
            <div className="bg-espresso p-6 text-white">
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <div className="w-16 h-16 rounded-2xl bg-terracotta/20 border-2 border-white/20 overflow-hidden shadow-lg">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <SafeIcon icon={FiUser} className="w-8 h-8 text-white/40" />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold truncate max-w-[200px]">{fullName || 'User'}</h3>
                  <p className="text-xs text-white/50 flex items-center gap-1">
                    <SafeIcon icon={FiMail} className="w-3 h-3" /> {user.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-cream-dark">
              <button
                onClick={() => setActiveTab('profile')}
                className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  activeTab === 'profile' ? 'text-terracotta border-b-2 border-terracotta' : 'text-espresso-light/40 hover:bg-cream/50'
                }`}
              >
                Profile Info
              </button>
              <button
                onClick={() => setActiveTab('password')}
                className={`flex-1 py-3 text-[10px] font-bold uppercase tracking-widest transition-colors ${
                  activeTab === 'password' ? 'text-terracotta border-b-2 border-terracotta' : 'text-espresso-light/40 hover:bg-cream/50'
                }`}
              >
                Security
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {activeTab === 'profile' ? (
                <form onSubmit={handleUpdateProfile} className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">Display Name</label>
                    <div className="relative">
                      <SafeIcon icon={FiUser} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso-light/30" />
                      <input
                        className="w-full pl-10 pr-4 py-2 bg-cream/20 border border-cream-dark rounded-xl text-sm focus:outline-none focus:border-terracotta"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Your Name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">
                      Choose Your Style
                    </label>
                    <div className="grid grid-cols-6 gap-2 mb-4">
                      {AVATAR_PRESETS.map((url, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setAvatarUrl(url)}
                          className={`w-10 h-10 rounded-lg overflow-hidden border-2 transition-all group relative ${
                            avatarUrl === url ? 'border-terracotta scale-110 shadow-md' : 'border-transparent hover:border-cream-dark'
                          }`}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover" />
                          {avatarUrl === url && (
                            <div className="absolute inset-0 bg-terracotta/20 flex items-center justify-center">
                              <SafeIcon icon={FiCheck} className="text-white w-4 h-4" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="relative">
                      <SafeIcon icon={FiCamera} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso-light/30" />
                      <input
                        className="w-full pl-10 pr-4 py-2 bg-cream/20 border border-cream-dark rounded-xl text-[10px] focus:outline-none focus:border-terracotta"
                        value={avatarUrl}
                        onChange={(e) => setAvatarUrl(e.target.value)}
                        placeholder="Or paste custom image URL..."
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-espresso text-white rounded-xl font-bold text-xs hover:bg-espresso-light transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    {loading ? 'Saving...' : success ? 'Saved!' : 'Save Changes'}
                    {success ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiSave} />}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleUpdatePassword} className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">New Password</label>
                    <div className="relative">
                      <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso-light/30" />
                      <input
                        type="password"
                        className="w-full pl-10 pr-4 py-2 bg-cream/20 border border-cream-dark rounded-xl text-sm focus:outline-none focus:border-terracotta"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-espresso-light/60 uppercase mb-2">Confirm Password</label>
                    <div className="relative">
                      <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso-light/30" />
                      <input
                        type="password"
                        className="w-full pl-10 pr-4 py-2 bg-cream/20 border border-cream-dark rounded-xl text-sm focus:outline-none focus:border-terracotta"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 bg-terracotta text-white rounded-xl font-bold text-xs hover:bg-terracotta/90 transition-all flex items-center justify-center gap-2 shadow-lg"
                  >
                    {loading ? 'Updating...' : success ? 'Password Updated!' : 'Update Password'}
                    {success ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiLock} />}
                  </button>
                </form>
              )}

              {error && (
                <div className="mt-4 p-3 bg-red-50 text-red-600 text-[10px] font-bold rounded-lg flex items-center gap-2">
                  <SafeIcon icon={FiAlertCircle} />
                  {error}
                </div>
              )}
            </div>

            <div className="p-4 bg-cream/10 border-t border-cream-dark text-center">
              <p className="text-[9px] text-espresso-light/40 uppercase tracking-widest font-bold">Secure Account Portal</p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AccountDropdown;